import React, { useState } from 'react';
import { Reorder } from 'framer-motion';
import { ArrowLeftIcon } from 'lucide-react';
import type { Deliverable } from '../../types/portal';
import { usePortal } from '../../contexts/PortalContext';
import { AddDeliverable } from '../../components/portal/AddDeliverable';
import { DeliverableAdmin } from '../../components/portal/DeliverableAdmin';
import { ProjectFields } from '../../components/portal/ProjectFields';
import { projectStatus } from '../../utils/status';
import { deliverableStage } from '../../utils/deliverables';
import { ClientLink } from '../../components/portal/ClientLink';
import { PortalTeam } from '../../components/portal/PortalTeam';
import { ProjectOptions } from '../../components/portal/ProjectOptions';
import { BUTTON_GHOST, BUTTON_QUIET, BUTTON_SOLID, FIELD } from '../../components/portal/adminStyles';
export function ProjectAdmin() {
  const {
    activeProject,
    goPortal,
    // duplicateProject stays available in context for a future project
    // options menu — it is simply not a prominent action here.
    saveAsTemplate,
    startPreview,
    archiveProject,
    reopenProject,
    setDeliverableOrder,
    notify
  } = usePortal();
  const [editing, setEditing] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [carryingId, setCarryingId] = useState<string | null>(null);
  const project = activeProject;
  const reviewCount = project.deliverables.filter(item => deliverableStage(item.status) === 'Review').length;
  const finalCount = project.deliverables.length - reviewCount;
  const hidden = project.deliverables.filter(item => !item.visibleToClient).length;
  function saveTemplate(event: React.FormEvent) {
    event.preventDefault();
    const name = templateName.trim();
    if (!name) return;
    saveAsTemplate(name);
    setTemplateName('');
    setSavingTemplate(false);
    notify(`Template saved · ${name}`);
  }
  return <div data-id="element-702" data-mp="22jytc639kcuo">
      <button type="button" onClick={() => goPortal('projects')} className="label group flex items-center gap-3 text-paper/40 transition-colors duration-150 ease-out hover:text-paper" data-id="element-703" data-mp="22k767xzdzi5v">
        <ArrowLeftIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:-translate-x-1" strokeWidth={1.25} data-id="element-704" data-mp="1xmp7t1izwlc4" />
        Client Projects
      </button>

      <header className="mt-10 border-b border-ruleDark pb-10" data-id="element-705" data-mp="2qbcpyo8g6ulg">
        <p className="label text-paper/40" data-id="element-706" data-mp="2q9jp5pkin4ld">Portal Project Admin</p>
        <h1 className="mt-6 font-display text-4xl leading-[0.95] tracking-tight sm:text-6xl" data-id="element-707" data-mp="2bdk83pffhu43">
          {project.client.toUpperCase()}
        </h1>
        <p className="mt-6 font-display text-xl text-paper/70" data-id="element-708" data-mp="zz6payzj84ux">{project.name}</p>
        <p className="label mt-5 text-paper/40" data-id="element-709" data-mp="101b2k7jbmg7v">
          {project.type} · {projectStatus(project)} · Updated {project.updated}
          {project.archived ? ' · Archived' : ''}
        </p>

        <div className="mt-10 flex flex-wrap items-center gap-5" data-id="element-710" data-mp="3j47tpw0y79lu">
          <button type="button" onClick={startPreview} className={BUTTON_SOLID} data-id="element-711" data-mp="3e5wj3j2twv2k">
            Preview as Client
          </button>
          <button type="button" onClick={() => setEditing(current => !current)} className={BUTTON_GHOST} data-id="element-712" data-mp="3s5xtbp4zheup">
            {editing ? 'Close Editor' : 'Edit Project'}
          </button>
          <button type="button" onClick={() => setSavingTemplate(current => !current)} className={BUTTON_GHOST} data-id="element-713" data-mp="2vggi6un8f0lm">
            Save as Template
          </button>
          {project.archived ? <button type="button" onClick={() => {
          reopenProject(project.id);
          notify('Project reopened · back in Active Projects');
        }} className={BUTTON_GHOST} data-id="element-714" data-mp="34gcxi5nqen40">
              Reopen Project
            </button> : <button type="button" onClick={() => setConfirmArchive(true)} className={BUTTON_GHOST} data-id="element-715" data-mp="2ph0xc67z133z">
              Archive Project
            </button>}
        </div>

        <ClientLink slug={project.slug} projectId={project.id} data-id="element-716" data-mp="343u5mrj2p8mo" />

        {confirmArchive && !project.archived && <div className="mt-8 max-w-xl border border-paper/30 px-6 py-6" data-id="element-717" data-mp="393qdx2yszm81">
            <p className="font-display text-2xl leading-snug" data-id="element-718" data-mp="3d4sb7e95to7d">Archive this project?</p>
            <p className="label-sm mt-4 leading-relaxed text-paper/40" data-id="element-719" data-mp="3d6n013njy0gq">
              It will be removed from Active Projects but its project history and data will be
              retained.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-6" data-id="element-720" data-mp="1npu97qjfs146">
              <button type="button" onClick={() => {
            archiveProject();
            setConfirmArchive(false);
            notify('Project archived · nothing deleted');
            goPortal('projects');
          }} className={BUTTON_SOLID} data-id="element-721" data-mp="1nrovb86g1z9u">
                Archive Project
              </button>
              <button type="button" onClick={() => setConfirmArchive(false)} className={BUTTON_QUIET} data-id="element-722" data-mp="1bfw7ygvc93fg">
                Cancel
              </button>
            </div>
          </div>}

        {savingTemplate && <form onSubmit={saveTemplate} className="mt-8 max-w-md" data-id="element-723" data-mp="1z6ifm71fklox">
            <label className="block" data-id="element-724" data-mp="1u5kxtvkc1tny">
              <span className="label block text-paper/40" data-id="element-725" data-mp="1u4jcmy9lrqsi">Template Name</span>
              <input value={templateName} onChange={event => setTemplateName(event.target.value)} placeholder="e.g. 6-Episode Filmed Podcast" autoFocus className={FIELD} data-id="element-726" data-mp="1p5zpiuj9w8yk" />
            </label>
            <div className="mt-6 flex items-center gap-6" data-id="element-727" data-mp="31xjkvvbts4tn">
              <button type="submit" className={BUTTON_SOLID} data-id="element-728" data-mp="31vyr47xc4pwu">
                Save Template
              </button>
              <button type="button" onClick={() => setSavingTemplate(false)} className={BUTTON_QUIET} data-id="element-729" data-mp="2rzlvv5m563zz">
                Cancel
              </button>
            </div>
          </form>}

        {editing && <ProjectFields onClose={() => setEditing(false)} data-id="element-730" data-mp="2i8iyadsf875g" />}
      </header>

      <section aria-labelledby="deliverables-admin" className="mt-14" data-id="element-731" data-mp="2n1c3t3vprjil">
        <div className="flex flex-wrap items-baseline justify-between gap-4 border-b border-ruleDark pb-4" data-id="element-732" data-mp="299npctxzsiyg">
          <h2 id="deliverables-admin" className="label m-0 text-paper/40" data-id="element-733" data-mp="2e7fkrxxuin7e">
            Deliverables
          </h2>
          <p className="label-sm m-0 text-paper/35" data-id="element-734" data-mp="1zc9c137d8aad">
            {reviewCount} in review · {finalCount} final delivery
            {hidden > 0 ? ` · ${hidden} hidden from client` : ''}
          </p>
        </div>

        {project.deliverables.length === 0 ? <p className="border-b border-ruleDark py-10 font-display text-xl text-paper/40" data-id="element-735" data-mp="1gmxictsz003m">
            No deliverables yet. Add whatever this project requires.
          </p> : <Reorder.Group as="ul" axis="y" values={project.deliverables} onReorder={(next: Deliverable[]) => setDeliverableOrder(next.map(item => item.id))}
      /* A recessed field while a row is lifted, so the empty slot reads as a gap. */ className={['m-0 list-none p-0 transition-colors duration-150 ease-out', carryingId ? 'bg-paper/[0.02]' : ''].join(' ')} data-id="element-736" data-mp="1pnc6e0xbjtat">
            {project.deliverables.map(item => <DeliverableAdmin key={item.id} deliverable={item} onLift={setCarryingId} data-id="element-737" data-mp="2nc5pjuexl1ew" />)}
          </Reorder.Group>}

        <AddDeliverable data-id="element-738" data-mp="2i9wwyz5z01db" />
      </section>

      <PortalTeam data-id="element-739" data-mp="13070vkcq0872" />
      <ProjectOptions data-id="element-740" data-mp="12ynk7sn5jv4r" />
    </div>;
}